/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { spacing } from '../../../shared/theme/spacing';
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../../shared/layout/breakpoints';
import { ModalScrollScreen } from '../../../shared/layout/ModalScrollScreen';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame';
import { ShellScreenLoading } from '../../../shared/layout/ShellScreenLoading';
import { OperationalConfirmModal } from '../../../shared/modals/OperationalConfirmModal';
import { settingsStyles } from '../../../settings/screens/settingsScreen.styles';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../../shell/session/hooks/useBranchPermission';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { generateArcaCsr, saveFiscalProfileRemote } from '../../infra/fiscalRepository';
import { shareOrDownloadCsr } from '../../infra/shareCsrFile';
import { iibbSelectionToValue, iibbToSelection, isValidCuitDigits, normalizeCuitDigits } from '../../domain/fiscalPresentation';
import type { IibbSelection } from '../../domain/fiscalPresentation';
import { isValidIsoDateString } from '../../domain/fiscalTypes';
import { getFiscalContext } from '../../infra/fiscalConfigRepo';
import type { ArcaEnvironment, FiscalProfile, TaxRegime } from '../../domain/fiscalTypes';
import { navigateInShell } from '../../../shell/navigation/rootNavigation';
import { useShellScreenHeaderRefresh } from '../../../shell/navigation/TabScreenRefreshContext';
import { loadArcaWizardDraft, saveArcaWizardDraft, clearArcaWizardDraft, resolveArcaWizardStep } from '../../infra/arcaWizardDraftStorage';
import type { ArcaWizardDraft } from '../../infra/arcaWizardDraftStorage';
import { connectArcaSetup } from '../../application/connectArcaSetup';
import { ArcaSetupIntentStep } from './setup/ArcaSetupIntentStep';
import { ArcaSetupDataStep } from './setup/ArcaSetupDataStep';
import { ArcaSetupConnectStep } from './setup/ArcaSetupConnectStep';

const STICKY_NAV_FALLBACK_HEIGHT = 76;

const STEPS = ['Para qué', 'Tus datos', 'Conectar'] as const;

export const ArcaSetupWizardScreen = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const isWide = windowWidth >= CONTENT_WIDE_BREAKPOINT;
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const businessNameInputRef = useRef<TextInput>(null);
  const certPemInputRef = useRef<TextInput>(null);
  const privateKeyInputRef = useRef<TextInput>(null);
  const initialStep = route.params?.initialStep as number | undefined;
  const cutoverToProduction = route.params?.cutoverToProduction === true;
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const branches = useSessionStore((s) => s.branches);
  const { canView, canEdit: canEditPermission } = useBranchPermission('BranchSettings');
  const { entitlements } = usePlanEntitlements();
  const canEdit = entitlements?.is_owner === true && canEditPermission;
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const arcaEnabled = isGlobalFeatureOn(globalFeatureFlags, 'arca_integrations');

  const [step, setStep] = useState(0);
  const [stickyNavHeight, setStickyNavHeight] = useState(STICKY_NAV_FALLBACK_HEIGHT);
  const [wizardViewportHeight, setWizardViewportHeight] = useState(0);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [taxRegime, setTaxRegime] = useState<TaxRegime>('monotributo');
  const [emitterCuit, setEmitterCuit] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [domicilioComercial, setDomicilioComercial] = useState('');
  const [iibbSelection, setIibbSelection] = useState<IibbSelection>('convenio');
  const [iibbNumero, setIibbNumero] = useState('');
  const [inicioActividades, setInicioActividades] = useState('');
  const [certPem, setCertPem] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [certMode, setCertMode] = useState<'generate' | 'manual'>('generate');
  const [csrBusy, setCsrBusy] = useState(false);
  const [csrGenerated, setCsrGenerated] = useState<{ cuit: string; environment: ArcaEnvironment } | null>(null);
  const [pointOfSale, setPointOfSale] = useState('');
  const [savedPointOfSale, setSavedPointOfSale] = useState('');
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<ArcaEnvironment>('produccion');
  const [monotributoCategory, setMonotributoCategory] = useState('A');
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [connected, setConnected] = useState<{ lastInvoiceNumber?: number; padronWarning?: string | null } | null>(null);
  const [justConnected, setJustConnected] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [fiscalProfile, setFiscalProfile] = useState<FiscalProfile | null>(null);
  const [needsRevalidation, setNeedsRevalidation] = useState(false);
  const [setupChangeConfirmVisible, setSetupChangeConfirmVisible] = useState(false);
  const [setupChangeConfirmLoading, setSetupChangeConfirmLoading] = useState(false);
  const [pendingAfterConfirm, setPendingAfterConfirm] = useState<'next' | 'connect' | null>(null);
  const autosaveEnabledRef = useRef(false);
  const savedTaxRegimeRef = useRef<TaxRegime | null>(null);
  const [profileReloadToken, setProfileReloadToken] = useState(0);

  const iibb = iibbSelectionToValue(iibbSelection, iibbNumero);
  const regimeChangedFromSaved = savedTaxRegimeRef.current != null && savedTaxRegimeRef.current !== taxRegime;
  const emitterCuitChangedFromSaved =
    fiscalProfile != null && normalizeCuitDigits(fiscalProfile.emitterCuit ?? '') !== normalizeCuitDigits(emitterCuit);
  const environmentChangedFromSaved = fiscalProfile != null && fiscalProfile.environment !== environment;
  const certificateIdentityChangedFromSaved = emitterCuitChangedFromSaved || environmentChangedFromSaved;
  const setupScopeChangedFromSaved = regimeChangedFromSaved || certificateIdentityChangedFromSaved;
  const pointOfSaleChanged = pointOfSale.trim() !== savedPointOfSale.trim();
  const csrMatchesCurrent = Boolean(
    csrGenerated &&
      csrGenerated.cuit === normalizeCuitDigits(emitterCuit) &&
      csrGenerated.environment === environment
  );

  const emitterReady =
    isValidCuitDigits(emitterCuit) &&
    businessName.trim().length >= 2 &&
    domicilioComercial.trim().length >= 3 &&
    iibb.trim().length >= 2 &&
    isValidIsoDateString(inicioActividades);

  const certReady = useMemo(() => {
    const certOk = certPem.trim().includes('BEGIN CERTIFICATE');
    const untouched = !certPem.trim() && !privateKeyPem.trim();
    if (
      untouched &&
      !certificateIdentityChangedFromSaved &&
      (fiscalProfile?.arcaKeySource != null || fiscalProfile?.setupStatus === 'validated')
    ) {
      return true;
    }
    if (certMode === 'generate') return certOk;
    return certOk && privateKeyPem.trim().includes('BEGIN');
  }, [certMode, certPem, certificateIdentityChangedFromSaved, fiscalProfile, privateKeyPem]);

  const pointOfSaleNumber = Number(pointOfSale);
  const pointOfSaleReady = Number.isFinite(pointOfSaleNumber) && pointOfSaleNumber > 0;
  const isValidatedSetup =
    fiscalProfile?.setupStatus === 'validated' && !needsRevalidation && !setupScopeChangedFromSaved && !pointOfSaleChanged;
  const connectDirty =
    setupScopeChangedFromSaved ||
    pointOfSaleChanged ||
    certPem.trim().length > 0 ||
    privateKeyPem.trim().length > 0;
  const showConnectAction = !justConnected && (!isValidatedSetup || connectDirty || !pointOfSaleReady);

  const buildDraftPayload = useCallback(
    (nextStep: number): Omit<ArcaWizardDraft, 'version' | 'savedAt' | 'organizationId'> => ({
      step: nextStep,
      taxRegime,
      emitterCuit,
      businessName,
      domicilioComercial,
      iibb,
      inicioActividades,
      certPem,
      certMode,
      branchPvMap: currentBranchId ? { [currentBranchId]: pointOfSale } : {},
      environment,
      monotributoCategory,
    }),
    [
      businessName,
      certMode,
      certPem,
      currentBranchId,
      domicilioComercial,
      emitterCuit,
      environment,
      iibb,
      inicioActividades,
      monotributoCategory,
      pointOfSale,
      taxRegime,
    ]
  );

  const persistDraft = useCallback(
    async (nextStep: number) => {
      if (!organizationId || !autosaveEnabledRef.current) return;
      try {
        await saveArcaWizardDraft(organizationId, buildDraftPayload(nextStep));
        setDraftSavedAt(new Date().toISOString());
      } catch {
        // AsyncStorage puede fallar en web si el borrador es muy grande; no bloqueamos la UI.
      }
    },
    [buildDraftPayload, organizationId]
  );

  useEffect(() => {
    autosaveEnabledRef.current = false;
    if (!arcaEnabled) {
      setLoadingProfile(false);
      return;
    }
    if (!currentBranchId) {
      setLoadingProfile(true);
      return;
    }

    let cancelled = false;
    setLoadingProfile(true);

    (async () => {
      let profileValidated = false;
      try {
        const fiscalCtx = await getFiscalContext(currentBranchId, { includeAllBranches: true });
        if (cancelled) return;

        const profile = fiscalCtx.profile;
        const orgId =
          profile?.organizationId ??
          fiscalCtx.branchFiscal?.organizationId ??
          fiscalCtx.branchesFiscal?.[0]?.organizationId ??
          null;
        if (orgId) setOrganizationId(orgId);

        const draftLoaded = orgId ? await loadArcaWizardDraft(orgId) : null;
        const isValidated = profile?.setupStatus === 'validated';
        profileValidated = isValidated;
        setFiscalProfile(profile);

        if (profile?.arcaCsrCuit) {
          setCsrGenerated({
            cuit: profile.arcaCsrCuit,
            environment: profile.arcaCsrEnvironment ?? profile.environment,
          });
        }
        if (profile?.arcaKeySource === 'manual') setCertMode('manual');

        const serverPv =
          fiscalCtx.branchesFiscal?.find((row) => row.branchId === currentBranchId)?.pointOfSale ??
          fiscalCtx.branchFiscal?.pointOfSale ??
          null;
        const serverPvText = serverPv != null ? String(serverPv) : '';
        setSavedPointOfSale(serverPvText);

        savedTaxRegimeRef.current = profile?.taxRegime ?? null;
        setNeedsRevalidation(false);

        if (profile) {
          setTaxRegime(profile.taxRegime);
          setEmitterCuit(profile.emitterCuit ?? '');
          setBusinessName(profile.businessName ?? '');
          setDomicilioComercial(profile.domicilioComercial ?? '');
          const profileIibb = iibbToSelection(profile.iibb);
          setIibbSelection(profileIibb.selection);
          setIibbNumero(profileIibb.numero);
          setInicioActividades(profile.inicioActividades ?? '');
          setEnvironment(profile.environment);
          if (profile.monotributoCategory) setMonotributoCategory(profile.monotributoCategory);
          if (isValidated) {
            setConnected({});
            if (orgId) await clearArcaWizardDraft(orgId);
            setDraftSavedAt(null);
          }
        }

        let nextPv = serverPvText;
        if (draftLoaded && !isValidated) {
          const draftIdentityMatchesProfile =
            !profile ||
            (normalizeCuitDigits(draftLoaded.emitterCuit) === normalizeCuitDigits(profile.emitterCuit ?? '') &&
              draftLoaded.environment === profile.environment);
          setTaxRegime(draftLoaded.taxRegime);
          if (draftLoaded.emitterCuit) setEmitterCuit(draftLoaded.emitterCuit);
          if (draftLoaded.businessName) setBusinessName(draftLoaded.businessName);
          if (draftLoaded.domicilioComercial) setDomicilioComercial(draftLoaded.domicilioComercial);
          if (draftLoaded.iibb) {
            const draftIibb = iibbToSelection(draftLoaded.iibb);
            setIibbSelection(draftIibb.selection);
            setIibbNumero(draftIibb.numero);
          }
          if (draftLoaded.inicioActividades) setInicioActividades(draftLoaded.inicioActividades);
          if (draftIdentityMatchesProfile) {
            if (draftLoaded.certPem) setCertPem(draftLoaded.certPem);
          } else {
            setCertPem('');
            setPrivateKeyPem('');
          }
          if (draftLoaded.certMode === 'manual') setCertMode('manual');
          if (currentBranchId && draftLoaded.branchPvMap[currentBranchId] != null) {
            nextPv = draftLoaded.branchPvMap[currentBranchId];
          }
          setEnvironment(draftLoaded.environment);
          if (draftLoaded.monotributoCategory) setMonotributoCategory(draftLoaded.monotributoCategory);
          setDraftSavedAt(draftLoaded.savedAt);
        }
        setPointOfSale(nextPv);

        const resolvedStep =
          initialStep != null && Number.isFinite(initialStep)
            ? Math.min(Math.max(Math.trunc(initialStep), 0), STEPS.length - 1)
            : resolveArcaWizardStep({
                setupStatus: profile?.setupStatus ?? null,
                draftStep: isValidated ? null : draftLoaded?.step,
                hasEmitterProfile: Boolean(
                  profile?.emitterCuit &&
                    profile?.businessName &&
                    profile?.domicilioComercial &&
                    profile?.iibb &&
                    profile?.inicioActividades
                ),
              });

        if (cutoverToProduction) {
          setEnvironment('produccion');
          setCertPem('');
          setPrivateKeyPem('');
          setConnected(null);
          setStep(2);
        } else {
          setStep(resolvedStep);
        }
      } catch {
        // Mantener defaults si falla la carga remota/local.
      } finally {
        if (!cancelled) {
          autosaveEnabledRef.current = !profileValidated;
          setLoadingProfile(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [arcaEnabled, currentBranchId, cutoverToProduction, initialStep, profileReloadToken]);

  const onHeaderRefresh = useCallback(() => {
    setProfileReloadToken((token) => token + 1);
  }, []);

  useShellScreenHeaderRefresh('ArcaIntegration', onHeaderRefresh);

  useEffect(() => {
    if (!organizationId || !autosaveEnabledRef.current) return;
    const timer = setTimeout(() => {
      void persistDraft(step);
    }, 400);
    return () => clearTimeout(timer);
  }, [organizationId, persistDraft, step, pointOfSale]);

  const profileInput = useCallback(
    () => ({
      taxRegime,
      emitterCuit: normalizeCuitDigits(emitterCuit),
      businessName: businessName.trim(),
      domicilioComercial: domicilioComercial.trim(),
      iibb: iibb.trim(),
      inicioActividades: inicioActividades.trim(),
      monotributoCategory: taxRegime === 'monotributo' ? monotributoCategory : null,
      environment,
    }),
    [businessName, domicilioComercial, emitterCuit, environment, iibb, inicioActividades, monotributoCategory, taxRegime]
  );

  const saveEmitter = useCallback(
    async (options?: { regimeChanged?: boolean; setupChanged?: boolean; withPointOfSale?: boolean }) => {
      if (!currentBranchId) return;
      const pv = Number(pointOfSale);
      await saveFiscalProfileRemote(
        currentBranchId,
        {
          ...profileInput(),
          ...(options?.withPointOfSale
            ? { pointOfSale: Number.isFinite(pv) && pv > 0 ? pv : null }
            : {}),
        },
        { regimeChanged: options?.regimeChanged }
      );
      const setupChanged =
        options?.regimeChanged === true ||
        options?.setupChanged === true ||
        ((fiscalProfile?.setupStatus === 'validated' || needsRevalidation) && setupScopeChangedFromSaved);
      if (options?.regimeChanged) savedTaxRegimeRef.current = taxRegime;
      setFiscalProfile((prev) =>
        prev
          ? {
              ...prev,
              ...profileInput(),
              ...(certificateIdentityChangedFromSaved ? { arcaKeySource: null } : {}),
              ...(setupChanged ? { setupStatus: 'draft' as const, setupValidatedAt: null, setupError: null } : {}),
            }
          : prev
      );
      if (setupChanged) {
        setNeedsRevalidation(true);
        setConnected(null);
        autosaveEnabledRef.current = true;
      }
    },
    [
      certificateIdentityChangedFromSaved,
      currentBranchId,
      fiscalProfile,
      needsRevalidation,
      pointOfSale,
      profileInput,
      setupScopeChangedFromSaved,
      taxRegime,
    ]
  );

  const needsSetupChangeConfirm =
    setupScopeChangedFromSaved && (fiscalProfile?.setupStatus === 'validated' || needsRevalidation);

  const goNext = async () => {
    if (step >= STEPS.length - 1) return;
    if (emitterReady) {
      try {
        setSaving(true);
        await saveEmitter({ regimeChanged: regimeChangedFromSaved, setupChanged: setupScopeChangedFromSaved });
      } catch (err: unknown) {
        presentUnexpectedFailure({ module: 'fiscal', op: 'arca_wizard.save_step', error: err, userMessage: 'No se pudo guardar.' });
        return;
      } finally {
        setSaving(false);
      }
    }
    const nextStep = step + 1;
    setStep(nextStep);
    void persistDraft(nextStep);
  };

  const handleConnect = async () => {
    if (!currentBranchId || !pointOfSaleReady) return;
    setConnecting(true);
    setConnectError(null);
    try {
      const result = await connectArcaSetup({
        branchId: currentBranchId,
        ...profileInput(),
        certPem: certPem.trim() || undefined,
        privateKeyPem: certMode === 'manual' ? privateKeyPem.trim() || undefined : undefined,
        pointOfSale: pointOfSaleNumber,
        regimeChanged: regimeChangedFromSaved,
      });
      if (!result.ok) {
        setConnectError(result.error);
        return;
      }
      setConnected({ lastInvoiceNumber: result.lastInvoiceNumber, padronWarning: result.padronWarning });
      setJustConnected(true);
      setNeedsRevalidation(false);
      setSavedPointOfSale(pointOfSale.trim());
      setFiscalProfile((prev) =>
        prev
          ? { ...prev, ...profileInput(), setupStatus: 'validated', setupValidatedAt: new Date().toISOString(), setupError: null }
          : prev
      );
      savedTaxRegimeRef.current = taxRegime;
      if (organizationId) {
        await clearArcaWizardDraft(organizationId);
        setDraftSavedAt(null);
        autosaveEnabledRef.current = false;
      }
    } catch (err: unknown) {
      setConnectError('No se pudo conectar.');
      presentUnexpectedFailure({ module: 'fiscal', op: 'arca_wizard.connect', error: err });
    } finally {
      setConnecting(false);
    }
  };

  const handleNext = async () => {
    if (!canEdit) {
      showInfo('Sin permiso', 'Solo la persona dueña puede cambiar esta configuración.');
      return;
    }
    if (step < STEPS.length - 1 && !canProceed) return;
    if (needsSetupChangeConfirm) {
      setPendingAfterConfirm(step < STEPS.length - 1 ? 'next' : 'connect');
      setSetupChangeConfirmVisible(true);
      return;
    }
    if (step < STEPS.length - 1) {
      await goNext();
      return;
    }
    await handleConnect();
  };

  const confirmSetupChange = async () => {
    setSetupChangeConfirmLoading(true);
    try {
      setSetupChangeConfirmVisible(false);
      const action = pendingAfterConfirm;
      setPendingAfterConfirm(null);
      if (action === 'connect') await handleConnect();
      else await goNext();
    } finally {
      setSetupChangeConfirmLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      const nextStep = step - 1;
      setStep(nextStep);
      void persistDraft(nextStep);
      return;
    }
    if (navigation.canGoBack()) navigation.goBack();
  };

  const handleGenerateCsr = async () => {
    if (!currentBranchId) return;
    if (!emitterReady) {
      showError('Tus datos', 'Completá tus datos antes de pedir el certificado.');
      return;
    }
    setCsrBusy(true);
    setConnectError(null);
    try {
      if (canEdit) await saveEmitter();
      const result = await generateArcaCsr(currentBranchId, { environment });
      if (!result.ok || !result.csrPem) {
        showError('Solicitud', result.error ?? 'No se pudo generar la solicitud.');
        return;
      }
      setCsrGenerated({
        cuit: result.cuit ?? normalizeCuitDigits(emitterCuit),
        environment: result.environment ?? environment,
      });
      const outcome = await shareOrDownloadCsr(
        result.csrPem,
        result.fileName ?? `vuelto-${normalizeCuitDigits(emitterCuit)}.csr`
      );
      if (outcome === 'ok') {
        showInfo('Solicitud lista', 'Se descargó el archivo. Subilo a ARCA y volvé con el certificado.');
      }
    } catch (err: unknown) {
      presentUnexpectedFailure({
        module: 'fiscal',
        op: 'arca_wizard.generate_csr',
        error: err,
        userMessage: 'No se pudo generar la solicitud.',
      });
    } finally {
      setCsrBusy(false);
    }
  };

  const canProceed =
    step === 0 ? true : step === 1 ? emitterReady : emitterReady && certReady && pointOfSaleReady;
  const onLastStep = step === STEPS.length - 1;
  const primaryBusy = saving || connecting;
  const primaryDisabled =
    onLastStep && !showConnectAction
      ? false
      : !canEdit || !arcaEnabled || primaryBusy || (step > 0 && !canProceed);
  let nextHint: string | null = null;
  if (!canEdit) {
    nextHint = 'Solo la persona dueña puede cambiar esta configuración.';
  } else if (!arcaEnabled) {
    nextHint = 'La facturación electrónica está apagada.';
  } else if (!canProceed) {
    if (step === 1 || (step === 2 && !emitterReady)) {
      nextHint = 'Completá CUIT, razón social, domicilio, ingresos brutos e inicio de actividades.';
    } else if (step === 2) {
      if (!pointOfSaleReady && certReady) nextHint = 'Cargá el punto de venta.';
      else if (pointOfSaleReady && !certReady) {
        nextHint = certMode === 'manual' ? 'Pegá el certificado y la clave.' : 'Pegá el certificado que te devolvió ARCA.';
      } else {
        nextHint = certMode === 'manual'
          ? 'Pegá el certificado, la clave y el punto de venta.'
          : 'Pegá el certificado y cargá el punto de venta.';
      }
    }
  }

  const finish = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigateInShell(navigation, 'FiscalHub');
  };

  if (!canView) {
    return (
      <View style={[styles.container, styles.gateOuter]}>
        <View style={styles.gateCard}>
          <Text style={styles.heading}>Sin permiso</Text>
          <Text style={styles.subheading}>Pedile al dueño que te habilite Ajustes de sucursal.</Text>
          <TouchableOpacity style={settingsStyles.primaryButton} onPress={() => navigation.goBack()} accessibilityRole="button">
            <Text style={settingsStyles.primaryButtonText}>Volver</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (loadingProfile) {
    return (
      <ShellContentFrame>
        <ShellScreenLoading label="Cargando facturación…" />
      </ShellContentFrame>
    );
  }

  const primaryLabel = onLastStep ? (showConnectAction ? 'Conectar con ARCA' : 'Listo') : 'Siguiente';

  return (
    <ShellContentFrame>
      <View
        style={styles.wizardViewport}
        onLayout={(event) => {
          const nextHeight = event.nativeEvent.layout.height;
          if (nextHeight > 0 && Math.abs(nextHeight - wizardViewportHeight) > 1) {
            setWizardViewportHeight(nextHeight);
          }
        }}
      >
        <ModalScrollScreen
          ref={scrollRef}
          contentRef={scrollContentRef}
          variant="settingsWide"
          contentMaxWidth={800}
          contentContainerStyle={[styles.content, { paddingBottom: stickyNavHeight + spacing.xl }]}
          keyboardBottomPadding={stickyNavHeight + spacing.lg}
          keyboardShouldPersistTaps="handled"
          style={[
            styles.scrollFlex,
            wizardViewportHeight > 0 ? { maxHeight: Math.max(wizardViewportHeight - stickyNavHeight, 160) } : null,
          ]}
          onRefresh={onHeaderRefresh}
        >
          <View style={styles.header}>
            {draftSavedAt && !isValidatedSetup ? (
              <Text style={styles.draftHint}>Borrador guardado. Podés cerrar y seguir después.</Text>
            ) : null}
          </View>

          {!arcaEnabled ? (
            <View style={styles.gateCard}>
              <Text style={styles.subheading}>La facturación electrónica está apagada.</Text>
            </View>
          ) : null}

          {isWide ? (
            <View style={styles.dStepper}>
              {STEPS.map((label, idx) => {
                const active = idx === step;
                const done = idx < step;
                return (
                  <React.Fragment key={label}>
                    {idx > 0 ? <View style={[styles.dStepLine, done && styles.stepLineDone]} /> : null}
                    <TouchableOpacity
                      style={styles.dStep}
                      onPress={() => setStep(idx)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      accessibilityLabel={label}
                    >
                      <View style={[styles.dStepNum, active && styles.stepDotActive, done && styles.stepDotDone]}>
                        {done ? (
                          <Ionicons name="checkmark" size={16} color={ticketColors.gold} />
                        ) : (
                          <Text style={[styles.dStepNumText, active && styles.stepDotTextActive]}>{idx + 1}</Text>
                        )}
                      </View>
                      <Text style={[styles.dStepLabel, active && styles.stepCaptionActive]}>{label}</Text>
                    </TouchableOpacity>
                  </React.Fragment>
                );
              })}
            </View>
          ) : (
            <View style={styles.mStepper}>
              <View style={styles.mStepperTrack}>
                {STEPS.map((label, idx) => {
                  const active = idx === step;
                  const done = idx < step;
                  return (
                    <React.Fragment key={label}>
                      {idx > 0 ? <View style={[styles.mStepLine, done && styles.stepLineDone]} /> : null}
                      <TouchableOpacity
                        style={styles.mStepNode}
                        onPress={() => setStep(idx)}
                        accessibilityRole="button"
                        accessibilityState={{ selected: active }}
                        accessibilityLabel={label}
                      >
                        <View style={[styles.mStepDot, active && styles.stepDotActive, done && styles.stepDotDone]}>
                          {done ? (
                            <Ionicons name="checkmark" size={14} color={ticketColors.gold} />
                          ) : (
                            <Text style={[styles.mStepDotText, active && styles.stepDotTextActive]}>{idx + 1}</Text>
                          )}
                        </View>
                        <Text style={[styles.mStepCaption, active && styles.stepCaptionActive]} numberOfLines={2}>
                          {label}
                        </Text>
                      </TouchableOpacity>
                    </React.Fragment>
                  );
                })}
              </View>
            </View>
          )}

          {step === 0 ? (
            <ArcaSetupIntentStep
              value={environment}
              disabled={!canEdit || !arcaEnabled}
              onChange={(value) => {
                if (value !== environment) {
                  setCertPem('');
                  setPrivateKeyPem('');
                  setConnected(null);
                  setJustConnected(false);
                }
                setEnvironment(value);
              }}
            />
          ) : null}

          {step === 1 ? (
            <ArcaSetupDataStep
              isWide={isWide}
              disabled={!canEdit || !arcaEnabled}
              taxRegime={taxRegime}
              monotributoCategory={monotributoCategory}
              onChangeRegime={(value) => {
                setTaxRegime(value);
                setJustConnected(false);
              }}
              onChangeCategory={setMonotributoCategory}
              emitterCuit={emitterCuit}
              onChangeCuit={(value) => {
                if (normalizeCuitDigits(value) !== normalizeCuitDigits(emitterCuit)) {
                  setCertPem('');
                  setPrivateKeyPem('');
                  setConnected(null);
                }
                setJustConnected(false);
                setEmitterCuit(value);
              }}
              businessName={businessName}
              onChangeBusinessName={setBusinessName}
              businessNameInputRef={businessNameInputRef}
              domicilioComercial={domicilioComercial}
              onChangeDomicilio={setDomicilioComercial}
              iibbSelection={iibbSelection}
              onChangeIibbSelection={setIibbSelection}
              iibbNumero={iibbNumero}
              onChangeIibbNumero={setIibbNumero}
              inicioActividades={inicioActividades}
              onChangeInicio={setInicioActividades}
            />
          ) : null}

          {step === 2 ? (
            <ArcaSetupConnectStep
              isWide={isWide}
              disabled={!canEdit || !arcaEnabled}
              environment={environment}
              certMode={certMode}
              onToggleCertMode={() => setCertMode((mode) => (mode === 'generate' ? 'manual' : 'generate'))}
              certPem={certPem}
              onChangeCert={(value) => {
                setCertPem(value);
                setJustConnected(false);
              }}
              certInputRef={certPemInputRef}
              privateKeyPem={privateKeyPem}
              onChangePrivateKey={(value) => {
                setPrivateKeyPem(value);
                setJustConnected(false);
              }}
              privateKeyInputRef={privateKeyInputRef}
              csrBusy={csrBusy}
              csrReady={csrMatchesCurrent}
              onDownloadCsr={() => void handleGenerateCsr()}
              pointOfSale={pointOfSale}
              onChangePointOfSale={(value) => {
                setPointOfSale(value);
                setConnected(null);
                setJustConnected(false);
              }}
              showOtherBranchesHint={branches.length > 1}
              connected={justConnected}
              lastInvoiceNumber={connected?.lastInvoiceNumber}
              padronWarning={connected?.padronWarning}
              error={connectError}
            />
          ) : null}

          <OperationalConfirmModal
            visible={setupChangeConfirmVisible}
            title="Cambiar datos de ARCA"
            message="Si cambiás esto, hay que volver a conectar antes de facturar."
            confirmLabel="Guardar y reconectar"
            destructive
            loading={setupChangeConfirmLoading}
            iconName="swap-horizontal-outline"
            onClose={() => {
              if (setupChangeConfirmLoading) return;
              setSetupChangeConfirmVisible(false);
              setPendingAfterConfirm(null);
            }}
            onConfirm={() => void confirmSetupChange()}
          />
        </ModalScrollScreen>

        <View
          onLayout={(event) => {
            const nextHeight = event.nativeEvent.layout.height;
            if (nextHeight > 0 && Math.abs(nextHeight - stickyNavHeight) > 1) {
              setStickyNavHeight(nextHeight);
            }
          }}
          style={[styles.stickyNav, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}
        >
          <View style={[styles.navRow, isWide && styles.navRowWide]}>
            <TouchableOpacity
              style={[settingsStyles.secondaryButton, styles.navBackButton, isWide && styles.navBackButtonWide]}
              onPress={handleBack}
              accessibilityRole="button"
            >
              <Text style={settingsStyles.secondaryButtonText}>{step === 0 ? 'Volver' : 'Anterior'}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                settingsStyles.primaryButton,
                styles.navPrimary,
                isWide && styles.navPrimaryWide,
                primaryDisabled && !primaryBusy && styles.navPrimaryDisabled,
                primaryBusy && styles.disabledButton,
              ]}
              onPress={() => {
                if (onLastStep && !showConnectAction) {
                  finish();
                  return;
                }
                void handleNext();
              }}
              disabled={primaryDisabled}
              accessibilityRole="button"
              accessibilityHint={nextHint ?? undefined}
              accessibilityState={{ disabled: primaryDisabled, busy: primaryBusy }}
              testID={onLastStep && showConnectAction ? 'fiscal.wizard.connect' : onLastStep ? 'fiscal.wizard.done' : 'fiscal.wizard.next'}
            >
              {primaryBusy ? (
                <ActivityIndicator color={ticketColors.goldInk} />
              ) : (
                <Text style={[settingsStyles.primaryButtonText, primaryDisabled && styles.navPrimaryTextDisabled]}>
                  {primaryLabel}
                </Text>
              )}
            </TouchableOpacity>
          </View>
          {nextHint ? (
            <Text style={styles.navHint} accessibilityLiveRegion="polite">
              {nextHint}
            </Text>
          ) : null}
        </View>
      </View>
    </ShellContentFrame>
  );
};

const styles = StyleSheet.create({
  wizardViewport: { flex: 1, minHeight: 0, overflow: 'hidden' },
  scrollFlex: { flex: 1 },
  stickyNav: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    backgroundColor: ticketColors.bg,
    gap: spacing.xs,
  },
  container: { flex: 1, backgroundColor: ticketColors.bg },
  gateOuter: { flex: 1, justifyContent: 'center', padding: spacing.lg },
  content: { padding: spacing.lg, gap: spacing.lg, paddingBottom: spacing.xxl },
  header: { gap: spacing.sm },
  heading: { ...typography.titleXL, color: ticketColors.ink },
  subheading: { ...typography.body, color: ticketColors.ink2 },
  draftHint: { ...typography.caption, color: ticketColors.ink3 },
  gateCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: spacing.md,
  },
  mStepper: { gap: spacing.sm },
  mStepperTrack: { flexDirection: 'row', alignItems: 'flex-start' },
  mStepNode: { flex: 1, alignItems: 'center', gap: 6, minWidth: 0 },
  mStepDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mStepDotText: { ...typography.caption, color: ticketColors.ink3, fontWeight: '700', fontSize: 12 },
  mStepLine: {
    flex: 0.35,
    height: 2,
    backgroundColor: ticketColors.rule,
    marginTop: 13,
    marginHorizontal: -2,
  },
  mStepCaption: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 14,
  },
  dStepper: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: spacing.xs },
  dStep: { flex: 1, alignItems: 'center', gap: spacing.sm, minWidth: 0 },
  dStepLine: {
    flex: 0.35,
    height: 2,
    backgroundColor: ticketColors.rule,
    marginTop: 15,
    marginHorizontal: -2,
  },
  dStepNum: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dStepNumText: { ...typography.caption, color: ticketColors.ink3, fontWeight: '700' },
  dStepLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 15,
    fontSize: 12,
  },
  stepDotActive: { borderColor: ticketColors.gold, backgroundColor: ticketColors.gold },
  stepDotDone: {
    borderColor: ticketColors.gold,
    backgroundColor: 'rgba(201, 162, 39, 0.12)',
  },
  stepDotTextActive: { color: ticketColors.goldInk },
  stepCaptionActive: { color: ticketColors.gold, fontWeight: '700' },
  stepLineDone: { backgroundColor: 'rgba(201, 162, 39, 0.35)' },
  disabledButton: { opacity: 0.5 },
  navPrimaryDisabled: {
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  navPrimaryTextDisabled: { color: ticketColors.ink2 },
  navRow: { flexDirection: 'row', gap: spacing.md },
  navRowWide: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    justifyContent: 'space-between',
  },
  navHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
  },
  navBackButton: { flex: 1, minHeight: 52 },
  navBackButtonWide: { flex: 0, minWidth: 150, paddingHorizontal: spacing.xl },
  navPrimary: { flex: 1, minHeight: 52 },
  navPrimaryWide: { flex: 0, minWidth: 220, paddingHorizontal: spacing.xl },
});
